// Form rules for a user, apart from the component that draws them.
// They mirror data/schemas/user.js in the backend and fail before the round
// trip; what comes back in `error.fields` is still the authority.

export const NAME_MAX = 120;
export const EMAIL_MAX = 180;
export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 100;

// The six the backend accepts, from ROLES in data/models/user/user.js.
//
// `cli` is NOT here, and that is not an oversight: it exists in
// core/authz/roles.js as a bundle for command-line tokens, but the User model
// rejects it. A role offered here that the API refuses is a form that fails on
// submit for a reason nobody can see.
//
// The descriptions are what each role is FOR, taken from roles.js. Picking a
// role is the most consequential field in this form and the code name alone
// does not say what it grants.
export const ROLE_OPTIONS = [
    { value: 'owner',        label: 'Owner',        hint: 'Sin permisos propios. Es el valor por defecto' },
    { value: 'technician',   label: 'Técnico',      hint: 'Programa: lee la organización y escribe tareas' },
    { value: 'admin',        label: 'Admin',        hint: 'El líder. Escribe todo salvo lo del webmaster' },
    { value: 'webmaster',    label: 'Webmaster',    hint: 'Todos los permisos, sin excepción' },
    { value: 'agent_master', label: 'Agente maestro', hint: 'No es una persona: planea y revisa entregas' },
    { value: 'agent_slave',  label: 'Agente esclavo', hint: 'No es una persona: toma tareas y las entrega' },
];

export const STATUS_OPTIONS = [
    { value: 2, label: 'Activo' },
    { value: 1, label: 'Inactivo' },
];

export const EMPTY_VALUES = {
    name: '', email: '', password: '', role: 'owner', status: 2,
};

export function valuesFrom(user) {
    return {
        name: user?.name ?? '',
        email: user?.email ?? '',
        password: '',
        role: user?.role ?? 'owner',
        status: user?.status ?? 2,
    };
}

// Deliberately loose, and it has to stay that way: the browser, this check and
// the backend's Joi all disagree at the margins of what an address is, and the
// only one that decides is the backend. This catches the typo, not the edge.
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// `withPassword` is true only when creating: POST requires a password and
// PATCH does not accept one at all.
export function validate(values, { withPassword }) {
    const errors = {};
    const name = values.name.trim();
    const email = values.email.trim();

    if (!name) errors.name = 'El nombre es requerido';
    else if (name.length > NAME_MAX) errors.name = `No puede pasar de ${NAME_MAX} caracteres`;

    if (!email) errors.email = 'El correo es requerido';
    else if (!LOOKS_LIKE_EMAIL.test(email)) errors.email = 'El correo no es válido';
    else if (email.length > EMAIL_MAX) errors.email = `No puede pasar de ${EMAIL_MAX} caracteres`;

    if (withPassword) {
        if (!values.password) errors.password = 'La contraseña es requerida';
        else if (values.password.length < PASSWORD_MIN) {
            errors.password = `Debe tener al menos ${PASSWORD_MIN} caracteres`;
        } else if (values.password.length > PASSWORD_MAX) {
            errors.password = `No puede pasar de ${PASSWORD_MAX} caracteres`;
        }
    }

    return errors;
}

// POST /admin/users. The email is lowercased here as well as by the backend:
// otherwise the row created from "Ana@X.com" reads back as "ana@x.com" and it
// looks like the panel changed it.
export function toCreatePayload(values) {
    return {
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        password: values.password,
        role: values.role,
        status: Number(values.status),
    };
}

// PATCH /admin/users/{id}. Carries NO password — the contract has no field for
// one, and no route in this API sets somebody else's password. And no `role`:
// that travels through PATCH /{id}/role, which is where the permission for it
// actually lives.
//
// It does not send `id` either. The published contract marks it required in
// the body, but the route builds it from the path (`{ ...req.body, id }`), so
// sending it would only open the door to a mismatch.
export function toUpdatePayload(values) {
    return {
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        status: Number(values.status),
    };
}

// Two steps: who the person is, and then what they can do. The second one
// carries the password and the role — the two fields that decide access — so
// they arrive together and not sprinkled among contact details.
export const CREATE_STEPS = [
    { title: 'Quién es', fields: ['name', 'email'] },
    { title: 'Cómo entra', fields: ['password', 'role', 'status'] },
];
